import { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { selectTaxProfileState } from '../selectors/tax.selector';
import { updateTaxProfile } from '../actions/tax.actions';
import { statesFullNames, canadaProvinces, australiaStates } from '../constants/countries';
import { useGuardianConfirmModal } from './useGuardianConfirmModal';
import { selectAccountInfo } from '../selectors/account.selector';

interface TaxProfileFormValues {
    addressLine1: string;
    addressLine2: string;
    addressCity: string;
    addressState: string;
    addressPostCode: string;
    mailingAddressDifferent: boolean;
    mailingAddressLine1: string;
    mailingAddressLine2: string;
    mailingAddressCity: string;
    mailingAddressState: string;
    mailingAddressPostCode: string;
    guardianFirstName: string;
    guardianLastName: string;
    guardianEmail: string;
    guardianPhoneNumber: string;
}

export const useTaxProfileForm = () => {
    const dispatch = useDispatch();
    const { taxProfile, updating } = useSelector(selectTaxProfileState);
    const accountInfo = useSelector(selectAccountInfo);
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const elapsedYears = useMemo(() => {
        if (!taxProfile?.dateOfBirth) return null;
        const birthDate = moment(taxProfile.dateOfBirth);
        const currentDate = moment();
        return currentDate.diff(birthDate, 'years');
    }, [accountInfo?.dateOfBirth]);

    const elapsedAccYears = useMemo(() => {
        if (!accountInfo?.dateOfBirth) return null;
        const birthDate = moment(accountInfo.dateOfBirth);
        const currentDate = moment();
        return currentDate.diff(birthDate, 'years');
    }, [accountInfo?.dateOfBirth]);

    const showGuardianForm = useMemo(() => {
        return Boolean(
            (elapsedYears !== null && elapsedYears < 18) ||
                (elapsedAccYears !== null && elapsedAccYears < 18)
        );
    }, [elapsedYears]);

    const validationSchema = useMemo(() => {
        const baseSchema = {
            addressLine1: yup
                .string()
                .max(
                    100,
                    t('errors.epic.validation.maxLength.v2', {
                        field: t('epic.esport.label.profile.address1'),
                        maxLength: 100
                    })
                )
                .required(t('errors.epic.validation.required')),
            addressLine2: yup.string().max(
                100,
                t('errors.epic.validation.maxLength.v2', {
                    field: t('epic.esport.label.profile.address2'),
                    maxLength: 100
                })
            ),
            addressCity: yup
                .string()
                .max(
                    100,
                    t('errors.epic.validation.maxLength.v2', {
                        field: t('epic.esport.label.payoutInfo.city'),
                        maxLength: 100
                    })
                )
                .required(t('errors.epic.validation.required')),
            addressState: yup.string().max(
                100,
                t('errors.epic.validation.maxLength.v2', {
                    field: t('epic.esport.label.payoutInfo.state'),
                    maxLength: 100
                })
            ),
            addressPostCode: yup.string().max(
                100,
                t('errors.epic.validation.maxLength.v2', {
                    field: t('epic.esport.label.payoutInfo.zip'),
                    maxLength: 100
                })
            ),
            mailingAddressLine1: yup
                .string()
                .max(
                    100,
                    t('errors.epic.validation.maxLength.v2', {
                        field: t('epic.esport.label.profile.address1'),
                        maxLength: 100
                    })
                )
                .when('mailingAddressDifferent', {
                    is: true,
                    then: (schema) => schema.required(t('errors.epic.validation.required')),
                    otherwise: (schema) => schema
                }),
            mailingAddressLine2: yup.string().max(
                100,
                t('errors.epic.validation.maxLength.v2', {
                    field: t('epic.esport.label.profile.address1'),
                    maxLength: 100
                })
            ),
            mailingAddressCity: yup
                .string()
                .max(
                    100,
                    t('errors.epic.validation.maxLength.v2', {
                        field: t('epic.esport.label.payoutInfo.city'),
                        maxLength: 100
                    })
                )
                .when('mailingAddressDifferent', {
                    is: true,
                    then: (schema) => schema.required(t('errors.epic.validation.required')),
                    otherwise: (schema) => schema
                }),
            mailingAddressState: yup.string().max(
                100,
                t('errors.epic.validation.maxLength.v2', {
                    field: t('epic.esport.label.payoutInfo.state'),
                    maxLength: 100
                })
            ),
            mailingAddressPostCode: yup.string().max(
                100,
                t('errors.epic.validation.maxLength.v2', {
                    field: t('epic.esport.label.payoutInfo.zip'),
                    maxLength: 100
                })
            )
        };

        const guardianFields = {
            guardianFirstName: yup.string().max(
                50,
                t('errors.epic.validation.maxLength.v2', {
                    field: t('epic.esport.label.profile.guardian.firstName'),
                    maxLength: 50
                })
            ),
            guardianLastName: yup.string().max(
                50,
                t('errors.epic.validation.maxLength.v2', {
                    field: t('epic.esport.label.profile.guardian.lastName'),
                    maxLength: 50
                })
            ),
            guardianEmail: yup.string().email(t('errors.epic.validation.email')),
            guardianPhoneNumber: yup.string().max(
                20,
                t('errors.epic.validation.maxLength.v2', {
                    field: t('epic.esport.label.profile.guardian.phone'),
                    maxLength: 20
                })
            )
        };

        if (showGuardianForm) {
            guardianFields.guardianFirstName = guardianFields.guardianFirstName.required(
                t('errors.epic.validation.required')
            );
            guardianFields.guardianLastName = guardianFields.guardianLastName.required(
                t('errors.epic.validation.required')
            );
            guardianFields.guardianEmail = guardianFields.guardianEmail.required(
                t('errors.epic.validation.required')
            );
            guardianFields.guardianPhoneNumber = guardianFields.guardianPhoneNumber.required(
                t('errors.epic.validation.required')
            );
        }

        return yup.object({
            ...baseSchema,
            ...guardianFields
        });
    }, [t, showGuardianForm]);

    const initialValues: TaxProfileFormValues = {
        addressLine1: taxProfile?.addressLine1 || '',
        addressLine2: taxProfile?.addressLine2 || '',
        addressCity: taxProfile?.addressCity || '',
        addressState: taxProfile?.addressState || '',
        addressPostCode: taxProfile?.addressPostCode || '',
        mailingAddressDifferent: taxProfile?.mailingAddressDifferent || false,
        mailingAddressLine1: taxProfile?.mailingAddressLine1 || '',
        mailingAddressLine2: taxProfile?.mailingAddressLine2 || '',
        mailingAddressCity: taxProfile?.mailingAddressCity || '',
        mailingAddressState: taxProfile?.mailingAddressState || '',
        mailingAddressPostCode: taxProfile?.mailingAddressPostCode || '',
        guardianFirstName: taxProfile?.guardianFirstName || '',
        guardianLastName: taxProfile?.guardianLastName || '',
        guardianEmail: taxProfile?.guardianEmail || '',
        guardianPhoneNumber: taxProfile?.guardianPhoneNumber || ''
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            handleSubmit(values);
        }
    });

    const hasGuardianInfoChanged = useMemo(() => {
        if (!taxProfile) return false;

        const guardianFields = [
            'guardianFirstName',
            'guardianLastName',
            'guardianEmail',
            'guardianPhoneNumber'
        ];
        return guardianFields.some((field) => {
            const currentValue = formik.values[field as keyof TaxProfileFormValues];
            const originalValue = taxProfile[field as keyof typeof taxProfile];
            return currentValue !== originalValue;
        });
    }, [formik.values, taxProfile]);

    const handleSubmit = useCallback(
        async (values: TaxProfileFormValues) => {
            if (!taxProfile?.recordIdentityKey) return;

            setIsSubmitting(true);
            try {
                const profilePatch: Record<string, any> = {
                    addressLine1: values.addressLine1,
                    addressLine2: values.addressLine2,
                    addressCity: values.addressCity,
                    addressState: values.addressState,
                    addressPostCode: values.addressPostCode,
                    mailingAddressDifferent: values.mailingAddressDifferent,
                    mailingAddressLine1: values.mailingAddressLine1,
                    mailingAddressLine2: values.mailingAddressLine2,
                    mailingAddressCity: values.mailingAddressCity,
                    mailingAddressState: values.mailingAddressState,
                    mailingAddressPostCode: values.mailingAddressPostCode,
                    guardianFirstName: values.guardianFirstName,
                    guardianLastName: values.guardianLastName,
                    guardianEmail: values.guardianEmail,
                    guardianPhoneNumber: values.guardianPhoneNumber
                };

                Object.keys(profilePatch).forEach(
                    (key) => profilePatch[key] === undefined && delete profilePatch[key]
                );
                // @ts-ignore
                await dispatch(updateTaxProfile(profilePatch as any));
            } finally {
                setIsSubmitting(false);
            }
        },
        [dispatch, taxProfile?.recordIdentityKey]
    );

    const {
        isModalOpen,
        handleOpenModal,
        handleCloseModal,
        handleConfirm,
        handleToggleDialog,
        modalContent
    } = useGuardianConfirmModal({
        onConfirm: () => handleSubmit(formik.values)
    });

    const handleFormSubmit = useCallback(
        async (values: TaxProfileFormValues) => {
            if (hasGuardianInfoChanged && showGuardianForm) {
                handleOpenModal();
            } else {
                await handleSubmit(values);
            }
        },
        [hasGuardianInfoChanged, showGuardianForm, handleSubmit, handleOpenModal]
    );

    formik.handleSubmit = useCallback(
        (e?: React.FormEvent<HTMLFormElement>) => {
            if (e) {
                e.preventDefault();
            }
            handleFormSubmit(formik.values);
        },
        [handleFormSubmit, formik.values]
    );

    const handleChangeFormValues = useCallback(
        (_: string, evt: any) => {
            formik.handleChange(evt);
            formik.setFieldTouched(evt.target.name, true, false);
        },
        [formik]
    );

    let stateList;
    let mailingStateList;
    switch (taxProfile?.addressCountryCode) {
        case 'US':
            stateList = statesFullNames;
            break;
        case 'CA':
            stateList = canadaProvinces;
            break;
        case 'AU':
            stateList = australiaStates;
            break;
        default:
            stateList = null;
    }

    switch (taxProfile?.mailingAddressCountryCode) {
        case 'US':
            mailingStateList = statesFullNames;
            break;
        case 'CA':
            mailingStateList = canadaProvinces;
            break;
        case 'AU':
            mailingStateList = australiaStates;
            break;
        default:
            mailingStateList = null;
    }

    const defaultState =
        stateList && stateList.find((label) => label?.value === taxProfile?.addressState);

    const defaultMailingState =
        stateList && stateList.find((label) => label?.value === taxProfile?.mailingAddressState);

    const isDiffCountry =
        !!taxProfile?.addressCountryCode &&
        !!taxProfile?.mailingAddressCountryCode &&
        taxProfile?.addressCountryCode !== taxProfile?.mailingAddressCountryCode;
    const isDisabled = Boolean(!taxProfile?.recordIdentityKey || updating || isSubmitting);

    return {
        formik,
        handleChangeFormValues,
        isDisabled,
        isDiffCountry,
        stateList,
        mailingStateList,
        defaultState,
        defaultMailingState,
        updating: updating || isSubmitting,
        elapsedYears,
        showGuardianForm,
        isGuardianConfirmModalOpen: isModalOpen,
        handleGuardianConfirmModalOpen: handleOpenModal,
        handleGuardianConfirmModalClose: handleCloseModal,
        handleGuardianConfirm: handleConfirm,
        handleGuardianModalToggle: handleToggleDialog,
        guardianModalContent: modalContent,
        hasGuardianInfoChanged
    };
};
